import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HiArrowLeft } from 'react-icons/hi';

import { getPostBySlug, getPostProducts } from '../services/blogApi';
import { mapDbProduct } from '../utils/productMap';
import { trackEvent } from '../backend/utils/orderUtils';
/* Aliased: `trackEvent` above is the shop's Supabase logging, these are
   Google Analytics. */
import {
  trackAddToCart as gaAddToCart,
  trackEvent as gaEvent,
  trackSelectItem as gaSelectItem,
  trackViewItem as gaViewItem,
  trackViewItemList as gaViewItemList,
} from '../utils/analytics';
import { useCart } from '../context/CartContext';
import RichText from './RichText';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import ImageLightbox from './ImageLightbox';

import '../styles/Blog.css';

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [post, setPost] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [detailProduct, setDetailProduct] = useState(null);
  const [zoomProduct, setZoomProduct] = useState(null);
  const [justAdded, setJustAdded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await getPostBySlug(slug);
    setPost(data || null);

    if (data?.product_ids?.length) {
      const { data: prods } = await getPostProducts(data.product_ids);
      setProducts((prods || []).map(mapDbProduct));
    } else {
      setProducts([]);
    }

    setLoading(false);
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!justAdded) return;
    const t = setTimeout(() => setJustAdded(null), 1800);
    return () => clearTimeout(t);
  }, [justAdded]);

  /* The page view fired on the route change with a title guessed from
     the slug; now that the post has loaded, report the real one. */
  useEffect(() => {
    if (!post) return;

    document.title = `${post.title} — Celestial Stones`;
    gaEvent('blog_post_view', { post_slug: slug, post_title: post.title });
  }, [post, slug]);

  // Stones attached to the article — the whole point of the blog.
  useEffect(() => {
    if (!post || products.length === 0) return;
    gaViewItemList(`Blog — ${post.title}`, products);
  }, [post, products]);

  const listName = post ? `Blog — ${post.title}` : 'Blog';

  const openDetails = (product) => {
    setDetailProduct(product);
    gaSelectItem(product, listName);
    gaViewItem(product);
  };

  const handleAddToCart = (product) => {
    addItem(product, 1);
    setJustAdded({ id: product.id, at: Date.now() });
    trackEvent({
      eventType: 'add_to_cart',
      productId: product.supabaseId,
    }).catch(() => {});
    gaAddToCart(product, 1, listName);
  };

  if (loading) {
    return (
      <section className="blog-page">
        <div className="blog-article">
          <div className="blog-skeleton blog-skeleton-tall" />
        </div>
      </section>
    );
  }

  /* A draft, a deleted post, or a mistyped URL all land here — RLS
     hides anything unpublished, so there is nothing to distinguish. */
  if (!post) {
    return (
      <section className="blog-page">
        <div className="blog-article blog-missing">
          <h1>Article not found</h1>
          <p>This article may have been moved or isn't published yet.</p>
          <Link className="featured-btn" to="/blog">
            Back to the blog
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="blog-page">
      <article className="blog-article">
        <button className="back-to-home-btn" onClick={() => navigate('/blog')}>
          <HiArrowLeft />
          <span>All Articles</span>
        </button>

        <time className="blog-article-date">
          {fmtDate(post.published_at || post.created_at)}
        </time>
        <h1 className="blog-article-title">{post.title}</h1>
        {post.excerpt && <p className="blog-article-lead">{post.excerpt}</p>}

        {post.cover_image && (
          <img
            className="blog-article-cover"
            src={post.cover_image}
            alt={post.title}
          />
        )}

        <RichText text={post.body} className="blog-article-body" />

        {products.length > 0 && (
          <div className="blog-products">
            <h2>Stones in this article</h2>
            <div className="blog-products-grid">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  justAdded={justAdded}
                  onZoom={setZoomProduct}
                  onOpenDetails={openDetails}
                  onAddToCart={handleAddToCart}
                  compact
                />
              ))}
            </div>
          </div>
        )}

        <div className="blog-article-foot">
          <Link className="featured-btn" to="/shop">
            <span>Browse the full collection</span>
          </Link>
        </div>
      </article>

      {detailProduct && (
        <ProductModal
          product={detailProduct}
          onAddToCart={handleAddToCart}
          onClose={() => setDetailProduct(null)}
        />
      )}

      {zoomProduct && (
        <ImageLightbox
          src={zoomProduct.image}
          alt={zoomProduct.name}
          onClose={() => setZoomProduct(null)}
        />
      )}
    </section>
  );
}
